import type { AdminPermission, AdminRole } from "../../contracts/admin-permissions";
import type { AdminDashboardDependencies } from "../composition/create-admin-dashboard-dependencies";
import type { AdminDashboardLocale } from "../../shared/locale/admin-dashboard-locale";
import {
  formatAdminDashboardLocaleLabel,
  formatAdminDashboardLocaleOptionLabel,
} from "../../shared/locale/admin-dashboard-copy";
import { formatAdminAuthModeLabel } from "../../shared/locale/admin-dashboard-status-copy";

export type AdminShellNavigationItem = {
  label: string;
  to: string;
};

export type AdminShellNavigationSection = {
  items: AdminShellNavigationItem[];
  label: string;
};

export type AdminShellViewModel = {
  adminPermissions: AdminPermission[];
  adminRoles: AdminRole[];
  authModeLabel: string;
  bundleFingerprint: string;
  environmentName: string;
  locale: AdminDashboardLocale;
  localeLabel: string;
  localeOptions: Array<{
    key: AdminDashboardLocale;
    label: string;
  }>;
  navigationSections: AdminShellNavigationSection[];
  release: string;
  shellBadgeLabels: {
    authMode: string;
    bundle: string;
    environment: string;
    release: string;
  };
  shellCaption: string;
  shellCaptionEyebrow: string;
  shellSubtitle: string;
  shellTitle: string;
  shellTitleEyebrow: string;
  shellEyebrow: string;
  shellBrand: string;
};

function toPrimaryNavigationItems(locale: AdminDashboardLocale): AdminShellNavigationItem[] {
  return locale === "ko"
    ? [
        { label: "개요", to: "/admin" },
        { label: "사용자", to: "/admin/users" },
        { label: "실기기 지표", to: "/admin/device-kpis" },
        { label: "오류 흐름", to: "/admin/failed-flows" },
      ]
    : [
        { label: "Overview", to: "/admin" },
        { label: "Users", to: "/admin/users" },
        { label: "Device KPIs", to: "/admin/device-kpis" },
        { label: "Failed Flows", to: "/admin/failed-flows" },
      ];
}

function toSupportingNavigationItems(locale: AdminDashboardLocale): AdminShellNavigationItem[] {
  return locale === "ko"
    ? [
        { label: "작업", to: "/admin/jobs" },
        { label: "릴리스", to: "/admin/releases" },
      ]
    : [
        { label: "Jobs", to: "/admin/jobs" },
        { label: "Releases", to: "/admin/releases" },
      ];
}

export function createAdminShellViewModel(
  dependencies: AdminDashboardDependencies,
  locale: AdminDashboardLocale,
): AdminShellViewModel {
  const isKorean = locale === "ko";

  return {
    environmentName: dependencies.runtimeConfig.environmentName,
    release: dependencies.runtimeConfig.release ?? "dev-local",
    bundleFingerprint: dependencies.runtimeConfig.bundleFingerprint,
    authModeLabel: formatAdminAuthModeLabel(dependencies.runtimeConfig.authMode, locale),
    locale,
    localeLabel: formatAdminDashboardLocaleLabel(locale),
    localeOptions: [
      { key: "ko", label: formatAdminDashboardLocaleOptionLabel(locale, "ko") },
      { key: "en", label: formatAdminDashboardLocaleOptionLabel(locale, "en") },
    ],
    adminRoles: dependencies.adminRoles,
    adminPermissions: dependencies.adminPermissions,
    navigationSections: [
      {
        label: isKorean ? "주요 운영 흐름" : "Primary workflows",
        items: toPrimaryNavigationItems(locale),
      },
      {
        label: isKorean ? "보조 상세 화면" : "Supporting drill-downs",
        items: toSupportingNavigationItems(locale),
      },
    ],
    shellEyebrow: isKorean ? "PetLog 관리자" : "PetLog Admin",
    shellBrand: isKorean ? "관리자 콘솔" : "Admin Console",
    shellCaptionEyebrow: isKorean ? "운영 가이드" : "Operator guide",
    shellTitleEyebrow: isKorean ? "관리자 운영" : "Admin Operations",
    shellTitle: isKorean ? "관리자 운영 콘솔" : "Admin operations console",
    shellSubtitle: isKorean
      ? "세 가지 주요 축(사용자, 실기기 지표, 실패 추적)과 보조 상세 화면(작업, 릴리스)을 같은 화면에서 유지합니다."
      : "Keep the three primary axes (user, install KPI, failed trace) and supporting drill-downs (Jobs, Releases) in one frame.",
    shellCaption: isKorean
      ? "운영자는 이 콘솔에서 사용자 저장 상태, 설치본 흐름, 실패 추적, 릴리스 회귀를 순서 있게 판단합니다."
      : "Operators use this console to review user storage state, install funnel, failure traces, and release regression in a fixed order.",
    shellBadgeLabels: {
      authMode: isKorean ? "인증" : "Auth",
      bundle: isKorean ? "번들" : "Bundle",
      environment: isKorean ? "환경" : "Env",
      release: isKorean ? "릴리스" : "Release",
    },
  };
}
